import React from 'react';
import { Info, TrendingUp, DollarSign, PiggyBank, Eye, AlertTriangle, ShoppingCart } from 'lucide-react';

/**
 * Компонент с информацией об инфляции и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-red-500" />
        Всё об инфляции: причины, последствия, защита
      </h2>
      
      <div className="prose prose-red dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-red-500" />
          Что такое инфляция?
        </h3>
        <p>
          <strong>Инфляция</strong> — это устойчивый рост общего уровня цен на товары и услуги в экономике. Ключевое слово здесь — «общего»: цены на отдельные товары могут и снижаться, но если большинство товаров и услуг становятся дороже, это инфляция. 
          Инфляция измеряется как процентное изменение общего уровня цен за определенный период, обычно за год.
        </p>

        <h3 className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-red-500" />
          Как инфляция влияет на покупательную способность?
        </h3>
        <p>
          Основное последствие инфляции — снижение покупательной способности денег. Если годовая инфляция составляет 7%, это означает, что через год на ту же сумму можно купить примерно на 7% меньше товаров и услуг.
        </p>
        <p>
          Важно понимать, что влияние инфляции накапливается с течением времени. Например, при среднегодовой инфляции 7% за 10 лет покупательная способность снизится почти на 50%. Это означает, что товар, который стоил 100 рублей 10 лет назад, сегодня будет стоить около 200 рублей.
        </p>

        <h3 className="flex items-center gap-2">
          <ShoppingCart className="w-5 h-5 text-red-500" />
          Инфляция в России: основные показатели
        </h3>
        <p>
          В России основным показателем инфляции является Индекс потребительских цен (ИПЦ), который рассчитывается Федеральной службой государственной статистики (Росстат). ИПЦ измеряет изменение стоимости фиксированной корзины товаров и услуг, которые покупает среднестатистический потребитель.
        </p>
        <p>
          За последние 20 лет инфляция в России значительно снизилась: от двузначных значений в начале 2000-х годов до 2-6% в 2017-2019 годах. Однако в 2021-2022 годах произошло ускорение инфляции в связи с пандемией COVID-19 и геополитическими факторами.
        </p>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Примеры инфляции в России</h4>
          <ul className="text-blue-600 dark:text-blue-400 text-sm mb-0 pl-5 list-disc">
            <li>2022 год: 11.9%</li>
            <li>2021 год: 8.4%</li>
            <li>2020 год: 4.9%</li>
            <li>2019 год: 3.0%</li>
            <li>2018 год: 4.3%</li>
            <li>2000-2022: среднегодовая инфляция около 9.4%</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-red-500" />
          Различные типы инфляции
        </h3>
        <ul>
          <li>
            <strong>Умеренная инфляция</strong> (2-3% в год) считается нормальной и даже полезной для экономики, так как стимулирует спрос.
          </li>
          <li>
            <strong>Галопирующая инфляция</strong> (10-50% в год) создает серьезные экономические проблемы и требует активных мер со стороны государства.
          </li>
          <li>
            <strong>Гиперинфляция</strong> (более 50% в месяц) ведет к разрушению денежной системы и экономическому кризису.
          </li>
          <li>
            <strong>Дефляция</strong> — обратный процесс, когда общий уровень цен снижается. Хотя это может показаться положительным явлением, длительная дефляция часто приводит к экономической стагнации.
          </li>
        </ul>
        
        <h3 className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          Причины инфляции
        </h3>
        <p>
          Инфляция может возникать по разным причинам:
        </p>
        <ul>
          <li>
            <strong>Инфляция спроса</strong> — возникает, когда совокупный спрос превышает предложение. Например, при снижении процентных ставок люди берут больше кредитов и больше тратят.
          </li>
          <li>
            <strong>Инфляция издержек</strong> — возникает из-за роста затрат на производство товаров и услуг. Например, рост цен на энергоносители или повышение заработной платы.
          </li>
          <li>
            <strong>Монетарная инфляция</strong> — вызвана избыточным увеличением денежной массы, не подкрепленным соответствующим ростом производства.
          </li>
          <li>
            <strong>Импортируемая инфляция</strong> — возникает из-за роста цен на импортные товары или ослабления национальной валюты.
          </li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Важно знать об инфляции в России
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0 pl-5 list-disc">
            <li>Инфляция неравномерно затрагивает разные категории товаров и услуг</li>
            <li>Цены на продукты питания и медицинские услуги часто растут быстрее общей инфляции</li>
            <li>Официальная инфляция может отличаться от инфляции, которую «ощущает» конкретный человек, зависящей от его потребительских привычек</li>
            <li>В крупных городах, особенно в Москве и Санкт-Петербурге, инфляция часто выше среднероссийских показателей</li>
            <li>Банк России придерживается таргетированной инфляции (цель — около 4% в год)</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-red-500" />
          Как защитить сбережения от инфляции?
        </h3>
        <p>
          Существует несколько стратегий защиты от инфляции:
        </p>
        <ol>
          <li>
            <strong>Банковские вклады</strong> — простой способ частичной защиты от инфляции. Ключевое правило: процентная ставка по вкладу должна быть выше или сопоставима с уровнем инфляции.
          </li>
          <li>
            <strong>Облигации</strong> — в том числе облигации федерального займа с защитой от инфляции (ОФЗ-ИН), доходность которых привязана к уровню инфляции.
          </li>
          <li>
            <strong>Недвижимость</strong> — традиционно считается хорошей защитой от инфляции, поскольку ее стоимость обычно растет вместе с общим уровнем цен.
          </li>
          <li>
            <strong>Акции</strong> — исторически обеспечивают доходность выше инфляции в долгосрочной перспективе, хотя и с более высоким риском.
          </li>
          <li>
            <strong>Товары-защитные активы</strong> — золото, серебро и другие драгоценные металлы часто используются как защита от инфляции.
          </li>
          <li>
            <strong>Диверсификация</strong> — распределение сбережений между разными активами снижает риски и повышает шансы на сохранение покупательной способности.
          </li>
        </ol>

        <p>
          Важно помнить, что любые инвестиции связаны с определенными рисками. Выбор стратегии защиты от инфляции должен учитывать ваши финансовые цели, горизонт планирования и отношение к риску.
        </p>

        <h3>Инфляция и экономическая политика</h3>
        <p>
          Центральные банки, включая Банк России, используют различные инструменты для контроля инфляции:
        </p>
        <ul>
          <li><strong>Ключевая ставка</strong> — повышение ставки делает кредиты дороже, что снижает потребительский спрос и замедляет инфляцию</li>
          <li><strong>Операции на открытом рынке</strong> — покупка и продажа государственных ценных бумаг</li>
          <li><strong>Нормы обязательных резервов</strong> — регулирование объема средств, которые банки должны держать в резерве</li>
          <li><strong>Валютные интервенции</strong> — покупка и продажа иностранной валюты для стабилизации курса национальной валюты</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор инфляции помогает понять, как обесцениваются деньги с течением времени, и принимать более обоснованные финансовые решения.
        </p>
      </div>
    </article>
  );
};

export default InfoContent;